import { useEffect } from 'react';
import {
  FieldError,
  Validate,
  ValidationRule,
  useFormContext,
} from 'react-hook-form';
import { restrictToOnlyLetters } from '../../helpers';

export default function FormInput({
  label,
  name,
  required,
  minLength,
  maxLength,
  min,
  max,
  pattern,
  validate,
  placeholder,
  type = 'text',
  desktopWidth,
  onChange,
  controller,
  disabled,
  positionError,
  lettersOnly,
  minimalisticDesign,
  labelOnBg,
}: {
  label?: string;
  name: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  minimalisticDesign?: boolean;
  positionError?: boolean;
  pattern?: ValidationRule<RegExp>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate?: Validate<any, any> | Record<string, Validate<any, any>>;
  placeholder: string;
  type?: 'text' | 'tel' | 'email' | 'number' | 'hidden';
  novalidate?: boolean;
  desktopWidth?: string;
  controller?: {
    setValue(value: string): void;
    value: string;
  };
  onChange?(value: string): void;
  disabled?: boolean;
  lettersOnly?: boolean;
  labelOnBg?: boolean;
}): JSX.Element {
  const {
    setValue: setFormContextValue,
    formState,
    register: formContextRegister,
  } = useFormContext();
  let error = formState.errors?.[name] as FieldError | undefined;
  const controllerValue = controller?.value;

  useEffect(() => {
    setFormContextValue(name, controllerValue, {
      shouldDirty: true,
    });
  }, [controllerValue, setFormContextValue, name]);

  const { onChange: formContextRegOnChange, ...formContextRegRest } =
    formContextRegister(name, {
      required,
      minLength,
      min,
      maxLength,
      max,
      pattern,
      validate,
    });

  return (
    <div
      className={`input-form-container self-start md:px-2 relative w-full rounded-md ${
        name === 'SelectedCities' || name === 'InterestedIn'
          ? ''
          : 'my-5 md:my-3'
      } ${type}${desktopWidth === 'full' ? ' md:w-full' : ' md:w-1/2'}`}
    >
      <input
        type={type}
        readOnly={disabled}
        {...formContextRegRest}
        placeholder={placeholder}
        required={required}
        onChange={(e) => {
          error = undefined;
          formContextRegOnChange(e);
          onChange?.(e.target.value);
          controller?.setValue?.(e.target.value);
        }}
        className={`${
          minimalisticDesign ? 'border-b-1 !p-2' : 'border-1 rounded-md'
        } w-full inputText outline-none font-ic ${
          name === 'SelectedCities' || name === 'InterestedIn' ? 'hidden' : ''
        } ${
          error
            ? 'border-red500'
            : 'focus-within:bg-white border-grey100 focus-within:border-grey300'
        }`}
        onKeyDown={(e) => {
          restrictToOnlyLetters(e, lettersOnly);
        }}
      />
      <p
        className={`m-0 absolute text-red500 text-tiny ${
          positionError === true
            ? 'z-10 px-1 md:px-2 top-0 left-2 md:left-4 transform translate-y-1/2- bg-white'
            : ''
        }`}
      >
        {error?.type === 'required' && 'Fältet är obligatoriskt'}
        {error?.type === 'minLength' && 'För få antal tecken'}
        {error?.type === 'maxLength' && 'För många antal tecken'}
        {type === 'tel' &&
          error?.type === 'pattern' &&
          `Fyll i ett giltigt telefonnummer`}
        {name === 'email' &&
          error?.type === 'pattern' &&
          `Fyll i en giltig e-postadress`}
        {name === 'zipCode' &&
          error?.type === 'pattern' &&
          `Ange giltigt postnr`}
        {name === 'city' && error?.type === 'pattern' && `Ange giltig postort`}
        {(name === 'firstname' || name === 'lastname') &&
          error?.type === 'pattern' &&
          `Kontrollera att namnet är korrekt`}
      </p>
      <label
        className={`px-1  bg-white left-0 font-ic md:left-4 absolute text-left floating-label ${
          minimalisticDesign ? 'minimalistic' : ''
        } ${
          name === 'SelectedCities' || name === 'InterestedIn' ? 'hidden' : ''
        } ${error ? 'text-red500' : 'text-black700'} ${
          labelOnBg ? 'label-on-bg' : ''
        }`}
      >
        {label}
      </label>
    </div>
  );
}
